import { useMutation, QueryClient } from "@tanstack/react-query";
import { type CardActivationFormData } from "@/features/card/schemas/forms/cardActivationForm.schema";
import {
  activateCard as activateCardService,
  lockCard as lockCardService,
  unlockCard as unlockCardService,
} from "@/features/card/services/cards.service";
import { Card, CardStatus, PutOnCardResponse } from "@/features/card/types/card.type";
import { useErrorHandler } from "@/shared/hooks/useErrorHandler";

const CARDS_MUTATION_META = { invalidateResources: ["cards"] } as const;

export type UseCardMutationReturn = {
  lockCard: (cardId: string) => void
  unlockCard: (cardId: string) => void
  activateCard: ({ cardId, data }: { cardId: string, data: CardActivationFormData }) => Promise<PutOnCardResponse>
  isUpdating: boolean
};

// Export for use in Pusher events - takes queryClient as parameter for testability
export const updateCardStatus = (queryClient: QueryClient, cardId: string, statusCode: CardStatus): void => {
  queryClient.setQueryData<Card[]>(["cards"], (oldData) => {
    if (!oldData) return oldData;
    return oldData.map((card) =>
      card.id === cardId
        ? { ...card, status_code: statusCode }
        : card,
    );
  });
};

export const useCardMutations = (onApiError?: () => void): UseCardMutationReturn => {
  const { handleError } = useErrorHandler();

  const lockCardMutation = useMutation({
    mutationFn: lockCardService,
    onError: (error) => {
      onApiError?.();
      handleError(error, {
        title: "Erreur lors du verrouillage de la carte",
        description: "Nous n'avons pas réussi à verrouiller votre carte, veuillez réessayer",
      });
    },
  });

  const unlockCardMutation = useMutation({
    mutationFn: unlockCardService,
    onError: (error) => {
      onApiError?.();
      handleError(error, {
        title: "Erreur lors du déverrouillage de la carte",
        description: "Nous n'avons pas réussi à déverrouiller votre carte, veuillez réessayer",
      });
    },
  });

  const activateCardMutation = useMutation({
    mutationFn: ({ cardId, data }: { cardId: string, data: CardActivationFormData }) => activateCardService(cardId, data),
    meta: CARDS_MUTATION_META,
    onError: (error) => {
      onApiError?.();
      handleError(error, {
        title: "Erreur lors d'activation de la carte",
        description: "Le code d'activation que vous avez saisi semble incorrect, veuillez réessayer",
      });
    },
  });

  return {
    lockCard: lockCardMutation.mutate,
    unlockCard: unlockCardMutation.mutate,
    activateCard: activateCardMutation.mutateAsync,
    isUpdating: lockCardMutation.isPending || unlockCardMutation.isPending || activateCardMutation.isPending,
  };
};
